import {
  createRemotePresenceCursorLabelStyle,
  createRemotePresenceGuestLabelStyles,
  isExternalGuestPresenceUser,
  remotePresenceGuestBadgeLabel,
} from '@blocksuite/affine-shared/utils';
import type { BaseSelection, UserInfo } from '@blocksuite/store';
import { html } from 'lit';
import type { DirectiveResult } from 'lit/directive.js';
import { styleMap, type StyleMapDirective } from 'lit/directives/style-map.js';

import type { SelectionRect } from './doc-remote-selection.js';

export const REMOTE_CURSOR_TRANSIENT_GRACE_MS = 2000;

type TableCellWithInlineEditor = Element & {
  inlineEditor?: {
    isValidInlineRange(inlineRange: { index: number; length: number }): boolean;
    toDomRange(inlineRange: { index: number; length: number }): Range | null;
  };
};

type TableBlockView = {
  model: { flavour: string };
  querySelector(selectors: string): Element | null;
};

type AnchoredTableSelection = BaseSelection & {
  data: {
    editing?: {
      rowId: string;
      columnId: string;
      inlineRange: { index: number; length: number };
    };
  };
};

function isAnchoredTableSelection(
  selection: BaseSelection
): selection is AnchoredTableSelection {
  if (selection.type !== 'table') return false;
  const data = (selection as Partial<AnchoredTableSelection>).data;
  return data?.editing != null;
}

export function resolveTableSelectionCaretRange(
  selections: BaseSelection[],
  getBlock: (blockId: string) => TableBlockView | null
): Range | null {
  const tableSelection = selections.find(isAnchoredTableSelection);
  if (!tableSelection) return null;

  const editing = tableSelection.data.editing;
  if (!editing) return null;

  const block = getBlock(tableSelection.blockId);
  if (!block || block.model.flavour !== 'affine:table') return null;

  const rowId = CSS.escape(editing.rowId);
  const columnId = CSS.escape(editing.columnId);
  const cell = block.querySelector(
    `affine-table-cell[data-row-id="${rowId}"][data-column-id="${columnId}"]`
  ) as TableCellWithInlineEditor | null;
  const inlineEditor = cell?.inlineEditor;
  if (!inlineEditor) return null;

  const caretRange = {
    index: editing.inlineRange.index + editing.inlineRange.length,
    length: 0,
  };
  if (!inlineEditor.isValidInlineRange(caretRange)) return null;

  return inlineEditor.toDomRange(caretRange);
}

export function resolveStableRemoteCursorRect({
  current,
  editingSurfaceActive,
  previous,
  previousAgeMs = Number.POSITIVE_INFINITY,
  transientGraceMs = REMOTE_CURSOR_TRANSIENT_GRACE_MS,
  textSelectionActive,
}: {
  current: SelectionRect | null;
  editingSurfaceActive: boolean;
  previous: SelectionRect | null;
  previousAgeMs?: number;
  transientGraceMs?: number;
  textSelectionActive: boolean;
}): SelectionRect | null {
  if (current) return current;
  if (
    textSelectionActive ||
    editingSurfaceActive ||
    previousAgeMs <= transientGraceMs
  ) {
    return previous;
  }
  return null;
}

export function selectionStyle(
  rect: SelectionRect,
  color: string
): DirectiveResult<typeof StyleMapDirective> {
  return styleMap({
    position: 'absolute',
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    backgroundColor: rect.transparent ? 'transparent' : color,
    pointerEvent: 'none',
    opacity: '20%',
    borderRadius: '3px',
  });
}

export function cursorStyle(
  rect: SelectionRect,
  color: string
): DirectiveResult<typeof StyleMapDirective> {
  return styleMap({
    position: 'absolute',
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    backgroundColor: color,
    pointerEvent: 'none',
  });
}

export function cursorLabelStyle(
  cursorHeight: number,
  color: string,
  guest = false
): DirectiveResult<typeof StyleMapDirective> {
  return styleMap({
    ...createRemotePresenceCursorLabelStyle(color, {
      cursorHeight,
      placement: 'above',
    }),
    ...(guest ? createRemotePresenceGuestLabelStyles(color).label : {}),
  });
}

export function cursorLabelContent(user: UserInfo, color: string) {
  if (!isExternalGuestPresenceUser(user)) return user.name;
  const styles = createRemotePresenceGuestLabelStyles(color);
  return html`<span style="${styleMap(styles.name)}">${user.name}</span
    ><span
      data-testid="remote-doc-cursor-guest-badge"
      style="${styleMap(styles.badge)}"
      >${remotePresenceGuestBadgeLabel()}</span
    >`;
}
